import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, MapPin, UsersRound } from "lucide-react";
import { useLang } from "../../lib/lang";

/**
 * InstallationDisclosure — plain-English clarity about who the customer deals
 * with and who installs. Approved copy (Feb 2026):
 *
 *   "Theo-Oppolia Cabinet is your single point of contact in Canada for
 *    design consultation, product supply, and project coordination.
 *    Installation is performed by our vetted partner contractors or an
 *    installer of your choice — you remain in control of who enters your home."
 *
 * This component surfaces that disclosure on the homepage and anywhere else
 * we need to reinforce it (installation page, builder/developer funnel).
 */
export default function InstallationDisclosure() {
  const { t } = useLang();
  const pillars = [
    { icon: UsersRound,  label: t("disclosure.pillar.single") },
    { icon: ShieldCheck, label: t("disclosure.pillar.vetted") },
    { icon: MapPin,      label: t("disclosure.pillar.choice") },
  ];

  return (
    <section
      data-testid="installation-disclosure"
      className="bg-[#FDFBF7] border-y border-[#D7CFC4]"
    >
      <div className="container-x py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
        >
          <div className="md:col-span-5">
            <p className="overline text-[#B08858]">{t("disclosure.overline")}</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl mt-3 leading-[1.1] text-[#0A0A0A]">
              {t("disclosure.title_1")}{" "}
              <em className="not-italic text-[#B08858]">{t("disclosure.title_em")}</em>
            </h2>
          </div>
          <div className="md:col-span-7">
            <p
              className="text-[#0A0A0A]/80 text-base md:text-lg leading-relaxed"
              data-testid="disclosure-copy"
            >
              {t("disclosure.body")}
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                  <li
                    key={p.label}
                    className="flex items-start gap-3 border-l-2 border-[#B08858] pl-4 py-1"
                    data-testid={`disclosure-pillar-${i}`}
                  >
                    <Icon size={18} className="shrink-0 text-[#B08858] mt-0.5" aria-hidden="true" />
                    <span className="text-[13px] md:text-sm text-[#0A0A0A]/75 leading-snug">
                      {p.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
